import { PROJECT_ID_PLACEHOLDER } from 'common/constants';

export const APP_ROOT = '/';
export const IDP_LOGIN_ROUTE = '/idp-login';

type Modules =
  | 'auth'
  | 'insights'
  | 'planning'
  | 'reports'
  | 'social'
  | 'sosv'
  | 'user'
  | 'privacyPolicy'
  | 'terms'
  | 'cookies';
export const MODULE_ROUTES: Record<Modules, string> = {
  auth: '/auth',
  insights: `${APP_ROOT}insights/${PROJECT_ID_PLACEHOLDER}`,
  planning: `${APP_ROOT}planning/${PROJECT_ID_PLACEHOLDER}`,
  reports: '/reports',
  social: '/social',
  sosv: '/sosv',
  user: '/user',
  privacyPolicy: '/privacy-policy',
  terms: '/terms',
  cookies: '/cookies',
};

type Pages =
  | 'development'
  | 'root'
  | 'capture'
  | 'profileCapture'
  | 'analyze'
  | 'coach'
  | 'mir'
  | 'reports'
  | 'my_impact';
export const INSIGHTS_MODULE_ROUTES: Record<Pages, string> = {
  development: `${MODULE_ROUTES.insights}/development`,
  root: `${MODULE_ROUTES.insights}`,
  my_impact: `${MODULE_ROUTES.insights}/my-impact`,
  capture: `${MODULE_ROUTES.insights}/capture`,
  profileCapture: `${MODULE_ROUTES.insights}/profile-capture`,
  analyze: `${MODULE_ROUTES.insights}/analyze`,
  coach: `${MODULE_ROUTES.insights}/coach`,
  mir: `${MODULE_ROUTES.insights}/mir`,
  reports: `${MODULE_ROUTES.insights}/reports`,
};

type MyImpactModulePages = 'interaction_kpis' | 'collected_responses';
export const MY_IMPACT_MODULE_ROUTES: Record<MyImpactModulePages, string> = {
  interaction_kpis: `${INSIGHTS_MODULE_ROUTES.my_impact}/interaction-kpis`,
  collected_responses: `${INSIGHTS_MODULE_ROUTES.my_impact}/collected-responses`,
};

type DevelopmentPages =
  | 'root'
  | 'componentLibrary'
  | 'testingInsightsDetail'
  | 'surveyConfigurator'
  | 'powerbiSpike';

export const DEVELOPMENT_ROUTES: Record<DevelopmentPages, string> = {
  root: `${INSIGHTS_MODULE_ROUTES.development}`,
  componentLibrary: `${INSIGHTS_MODULE_ROUTES.development}/component-library`,
  testingInsightsDetail: `${INSIGHTS_MODULE_ROUTES.development}/testing-insights-detail`,
  surveyConfigurator: `${INSIGHTS_MODULE_ROUTES.development}/survey-configurator`,
  powerbiSpike: `${INSIGHTS_MODULE_ROUTES.development}/powerbi-spike`,
};

type CapturePages = 'root' | 'newActivity' | 'newInsight';
export const CAPTURE_ROUTES: Record<CapturePages, string> = {
  root: `${INSIGHTS_MODULE_ROUTES.capture}`,
  newActivity: `${INSIGHTS_MODULE_ROUTES.capture}/newactivity`,
  newInsight: `${INSIGHTS_MODULE_ROUTES.capture}/newinsight`,
};

// Build Profile Capture Routes from base Capture Routes Object
const modifiedCaptureRoutes = Object.fromEntries(
  Object.entries(CAPTURE_ROUTES).map(([key, value]) => {
    // Apply your modification logic to the value
    const modifiedValue = value.replace(CAPTURE_ROUTES.root, INSIGHTS_MODULE_ROUTES.profileCapture);
    return [key, modifiedValue];
  })
);

export const PROFILE_CAPTURE_ROUTES: Record<CapturePages, string> = {
  ...(modifiedCaptureRoutes as Record<CapturePages, string>),
};

type AnalyzePages =
  | 'root'
  | 'alignment'
  | 'insights'
  | 'interactions'
  | 'responseDetails'
  | 'themeAnalytics'
  | 'elsieai'
  | 'elsieaiV2'
  | 'mir';
export const ANALYZE_ROUTES: Record<AnalyzePages, string> = {
  root: `${INSIGHTS_MODULE_ROUTES.analyze}`,
  alignment: `${INSIGHTS_MODULE_ROUTES.analyze}/alignment`,
  insights: `${INSIGHTS_MODULE_ROUTES.analyze}/insights`,
  interactions: `${INSIGHTS_MODULE_ROUTES.analyze}/interaction`,
  responseDetails: `${INSIGHTS_MODULE_ROUTES.analyze}/response/details`, // This is a deprecated route, but it's still used for re-directs
  themeAnalytics: `${INSIGHTS_MODULE_ROUTES.analyze}/theme-analytics`,
  elsieai: `${INSIGHTS_MODULE_ROUTES.analyze}/elsieai`,
  elsieaiV2: `${INSIGHTS_MODULE_ROUTES.analyze}/v2-elsieai`,
  mir: `${INSIGHTS_MODULE_ROUTES.analyze}/mir`,
};

type AlignmentRoutes = 'root' | 'summary' | 'detail' | 'compare';
export const ALIGNMENT_ROUTES: Record<AlignmentRoutes, string> = {
  root: `${ANALYZE_ROUTES.alignment}`,
  summary: `${ANALYZE_ROUTES.alignment}/summary`,
  detail: `${ANALYZE_ROUTES.alignment}/detail`,
  compare: `${ANALYZE_ROUTES.alignment}/compare`,
};

type InsightsRoutes = 'root' | 'summary' | 'detail' | 'compare';
export const INSIGHTS_ROUTES: Record<InsightsRoutes, string> = {
  root: `${ANALYZE_ROUTES.insights}`,
  summary: `${ANALYZE_ROUTES.insights}/summary`,
  detail: `${ANALYZE_ROUTES.insights}/detail`,
  compare: `${ANALYZE_ROUTES.insights}/compare`,
};

type InteractionsRoutes = 'root';
export const INTERACTIONS_ROUTES: Record<InteractionsRoutes, string> = {
  root: `${ANALYZE_ROUTES.interactions}`,
};

type CoachRoutes =
  | 'root'
  | 'summary'
  | 'detail'
  | 'team_detail_interaction_kpis'
  | 'team_detail_collected_responses';
export const COACH_ROUTES: Record<CoachRoutes, string> = {
  root: `${INSIGHTS_MODULE_ROUTES.coach}`,
  summary: `${INSIGHTS_MODULE_ROUTES.coach}/summary`,
  detail: `${INSIGHTS_MODULE_ROUTES.coach}/detail`,
  team_detail_interaction_kpis: `${INSIGHTS_MODULE_ROUTES.coach}/team-member-details/interaction-kpis`,
  team_detail_collected_responses: `${INSIGHTS_MODULE_ROUTES.coach}/team-member-details/collected-responses`,
};

type ReportsRoutes = 'root' | 'library' | 'view' | 'editor' | 'wizard' | 'catalog' | 'configurator';
export const REPORTS_ROUTES: Record<ReportsRoutes, string> = {
  root: `${INSIGHTS_MODULE_ROUTES.reports}`,
  library: `${INSIGHTS_MODULE_ROUTES.reports}/library`,
  view: `${INSIGHTS_MODULE_ROUTES.reports}/view`,
  editor: `${INSIGHTS_MODULE_ROUTES.reports}/editor`,
  wizard: `${INSIGHTS_MODULE_ROUTES.reports}/wizard`,
  catalog: `${INSIGHTS_MODULE_ROUTES.reports}/widget/catalog`,
  configurator: `${INSIGHTS_MODULE_ROUTES.reports}/widget/configurator`,
};

type UserRoutes = 'root' | 'settings' | 'files';
export const USER_ROUTES: Record<UserRoutes, string> = {
  root: `${MODULE_ROUTES.user}`,
  settings: `${MODULE_ROUTES.user}/settings`,
  files: `${MODULE_ROUTES.user}/${PROJECT_ID_PLACEHOLDER}/files`,
};
